import { useEffect, useRef, useState } from 'react'
import { DayPicker } from 'react-day-picker'
import { Calendar } from '@untitledui/icons'
import clsx from 'clsx'
import useTranslation from '@/hooks/useTranslation'
import 'react-day-picker/style.css'

const LOCALE_MAP = {
    fr: () => import('react-day-picker/locale').then((m) => m.fr),
    en: () => import('react-day-picker/locale').then((m) => m.enUS),
    ja: () => import('react-day-picker/locale').then((m) => m.ja),
    de: () => import('react-day-picker/locale').then((m) => m.de),
    no: () => import('react-day-picker/locale').then((m) => m.nb),
    tr: () => import('react-day-picker/locale').then((m) => m.tr),
    zh: () => import('react-day-picker/locale').then((m) => m.zhCN),
    es: () => import('react-day-picker/locale').then((m) => m.es),
    th: () => import('react-day-picker/locale').then((m) => m.th),
    cs: () => import('react-day-picker/locale').then((m) => m.cs),
    is: () => import('react-day-picker/locale').then((m) => m.is),
}

export default function DatePickerInput({
    value,
    onChange,
    placeholder,
    error,
}) {
    const { language, t } = useTranslation()
    const [open, setOpen] = useState(false)
    const [locale, setLocale] = useState(undefined)
    const containerRef = useRef(null)

    useEffect(() => {
        const loader = LOCALE_MAP[language]
        if (loader) {
            loader().then(setLocale)
        }
    }, [language])

    useEffect(() => {
        function handleClickOutside(e) {
            if (
                containerRef.current &&
                !containerRef.current.contains(e.target)
            ) {
                setOpen(false)
            }
        }
        if (open) {
            document.addEventListener('mousedown', handleClickOutside)
        }
        return () =>
            document.removeEventListener('mousedown', handleClickOutside)
    }, [open])

    const toYmd = (d) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

    const parseYmd = (str) => {
        const [y, m, d] = str.split('-').map(Number)
        return new Date(y, m - 1, d)
    }

    const dateObj = value ? parseYmd(value) : null

    const formatted = dateObj
        ? new Intl.DateTimeFormat(language, {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
          }).format(dateObj)
        : ''

    const inputClass = clsx(
        'flex items-center justify-between gap-3 w-full p-3 rounded-[8px] bg-background-product border h-[45px] w-full cursor-pointer transition-[border-color] duration-200 select-none',
        error
            ? 'border-[#E84341]'
            : open
              ? 'border-[#3F81F7]'
              : 'border-transparent'
    )

    const labelClass = clsx(
        'text-base',
        value ? 'text-dark-blue' : 'text-dark-blue/40'
    )

    return (
        <div ref={containerRef} className="relative">
            <div onClick={() => setOpen(!open)} className={inputClass}>
                <span className={labelClass}>
                    {value ? formatted : placeholder}
                </span>

                <Calendar
                    size={16}
                    className="text-dark-blue shrink-0"
                />
            </div>

            {open && (
                <div className="absolute top-full left-0 mt-2 bg-white rounded-[8px] border border-gray-200 z-50 p-3">
                    <DayPicker
                        mode="single"
                        selected={dateObj}
                        onSelect={(date) => {
                            if (date) onChange(toYmd(date))
                            setOpen(false)
                        }}
                        locale={locale}
                        today={new Date()}
                        disabled={{
                            before: new Date(),
                            after: new Date(
                                Date.now() + 30 * 24 * 60 * 60 * 1000
                            ),
                        }}
                    />
                    <button
                        type="button"
                        onClick={() => {
                            onChange(toYmd(new Date()))
                            setOpen(false)
                        }}
                        className="w-full text-center text-sm text-dark-blue/60 hover:text-dark-blue pt-2 mt-1 border-t border-gray-100 cursor-pointer"
                    >
                        {t('common.today')}
                    </button>
                </div>
            )}

            {error && (
                <span className="text-xs text-[#E84341] mt-2">{error}</span>
            )}
        </div>
    )
}
